/**
 * Two independent cart-hygiene rules:
 *
 * RULE A — clears the cart AND resets client-side session storage when a visitor returns to the
 * storefront after clicking a "Buy now"-style button (Rapi Bundle's skip-cart checkout flow)
 * without completing the order. Without this, the cart (and any hidden gift/bundle item injected
 * into it) stays sitting in the cart, and a product tagged hidden-gift ends up visibly exposed as
 * a named line item — see knowledge/shopify/problem_resolution.md Problems 1-4 and 23-24 for the
 * full history of why this exists and why it's built the way it is.
 *
 * RULE B — removes a $0 product from the cart whenever it ends up the ONLY item in the cart —
 * including *immediately*, the moment a paid item is removed and leaves a $0 item stranded alone
 * (not just on the next page load). A lone free item (no paired paid product alongside it) is
 * always an orphaned/test state here, never a legitimate cart — see
 * knowledge/shopify/problem_resolution.md Problem 1 (main product priced at $0 for testing) and
 * the free-gift bundle logic (Problems 1-2): the gift is only ever meant to exist *alongside* a
 * paid product, never alone. Deliberately does NOT touch a $0 item accompanied by other items.
 *
 * RULE A mechanism — mark intent explicitly: a capture-phase click listener watches for clicks on
 * the "Acheter Maintenant" buy-now button (matched by visible text, case-insensitive) and writes
 * a `sessionStorage` flag *before* Rapi's own click handler navigates away. `sessionStorage`
 * survives every navigation type in the same tab, so it doesn't depend on referrer or caching
 * behavior — see Problem 24 for why the earlier `document.referrer`/`event.persisted` approach
 * had to be abandoned (unreliable on this site's third-party-script-heavy pages).
 *
 * RULE B mechanism — since a removal via the cart drawer doesn't navigate/reload the page,
 * `pageshow` alone won't catch it. This wraps `fetch` and `XMLHttpRequest` to detect *any* request
 * to a cart-mutating endpoint (add/change/update/clear), and re-checks Rule B right after it
 * completes — catching the "just removed the paid item, gift is now stranded" moment immediately,
 * not just on the next full page load. A reentrancy guard (`gismoOwnMutationInFlight`) stops this
 * from reacting to the script's *own* clear/change calls, which would otherwise re-trigger itself.
 *
 * Session reset scope (Rule A only): every `localStorage`/`sessionStorage` key and every cookie
 * JavaScript is actually allowed to read/write (`document.cookie` never exposes HttpOnly cookies
 * — Shopify's own cart/session cookie is HttpOnly by design and was never reachable here
 * regardless; that's a browser security boundary, not a gap in this script) — EXCEPT anything
 * matching `PRESERVE_NAME_PATTERNS` below (language/locale/currency/country), which is left
 * untouched on purpose so a visitor's language and currency choice survives the reset. Matching
 * is by substring on the key/cookie name, not an exact list of Shopify's own cookie names, so it
 * also catches whatever an app might name its own language/currency storage.
 *
 * Both rules deliberately do nothing on Shopify's own checkout/thank-you pages: those are
 * rendered by Shopify's checkout, not this theme's layout, so this script never runs there
 * regardless. After a genuinely completed order, Shopify has already emptied the cart
 * server-side, so both checks below are no-ops in that case.
 */
(function () {
  'use strict';

  var FLAG_KEY = 'gismoPendingCheckout';
  var DEBUG = true; // set to false once confirmed working live, or delete the console.log lines
  var gismoOwnMutationInFlight = false; // reentrancy guard, see file header

  function log() {
    if (DEBUG) console.log.apply(console, ['[gismo-cart-debug]'].concat(Array.prototype.slice.call(arguments)));
  }

  function onCheckoutPage() {
    return window.location.pathname.indexOf('/checkout') === 0;
  }

  // --- Rule A, step 1: mark intent when the buy-now button is clicked, before Rapi navigates away ---
  document.addEventListener(
    'click',
    function (event) {
      var target = event.target.closest('[data-button-variant], button, a');
      if (!target) return;
      var text = (target.textContent || '').trim().toLowerCase();
      if (text.indexOf('acheter') === -1) return; // not the buy-now button
      log('Buy-now button clicked (text="' + target.textContent.trim() + '") — setting checkout-intent flag.');
      try {
        sessionStorage.setItem(FLAG_KEY, '1');
      } catch (e) {
        log('Could not write sessionStorage:', e);
      }
    },
    true // capture phase — run before Rapi's own click handler can navigate away
  );

  // Anything matching one of these (substring, case-insensitive) is left untouched by
  // resetSiteSession() — language/locale/currency/country choices survive the reset on purpose.
  var PRESERVE_NAME_PATTERNS = [/local/i, /lang/i, /currency/i, /country/i, /geo/i];

  function shouldPreserve(name) {
    return PRESERVE_NAME_PATTERNS.some(function (pattern) { return pattern.test(name); });
  }

  function resetSiteSession() {
    var preserved = [];

    try {
      Object.keys(localStorage).forEach(function (key) {
        if (shouldPreserve(key)) { preserved.push('localStorage:' + key); return; }
        localStorage.removeItem(key);
      });
    } catch (e) {
      log('Could not clear localStorage:', e);
    }

    try {
      Object.keys(sessionStorage).forEach(function (key) {
        if (shouldPreserve(key)) { preserved.push('sessionStorage:' + key); return; }
        sessionStorage.removeItem(key);
      });
    } catch (e) {
      log('Could not clear sessionStorage:', e);
    }

    try {
      document.cookie.split(';').forEach(function (c) {
        var name = c.split('=')[0].trim();
        if (!name) return;
        if (shouldPreserve(name)) { preserved.push('cookie:' + name); return; }
        var expire = 'expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        document.cookie = name + '=; ' + expire;
        document.cookie = name + '=; ' + expire + ' domain=' + window.location.hostname + ';';
      });
    } catch (e) {
      log('Could not clear cookies:', e);
    }

    log('Session reset done (cart-related client storage cleared). Preserved (language/currency/etc., untouched on purpose):', preserved);
  }

  // NOTE: an earlier version tried to live-patch just the cart icon bubble by fetching
  // `?section_id=cart-icon-bubble` and swapping its HTML in — that broke the icon (wrong markup
  // got injected, un-clickable, unstyled). Root cause: `cart-icon-bubble` isn't a real standalone
  // section in this theme, it's inline markup inside header.liquid, so that URL never returned
  // what the code assumed. A plain reload is slightly less smooth but cannot corrupt the header.
  function reloadToReflectCart() {
    window.location.reload();
  }

  // --- Rule A: on return from an abandoned "buy now" checkout, clear the cart + reset session ---
  function checkRuleA(cart) {
    var pendingCheckout;
    try {
      pendingCheckout = sessionStorage.getItem(FLAG_KEY) === '1';
    } catch (e) {
      pendingCheckout = false;
    }
    if (!pendingCheckout) return false;

    try { sessionStorage.removeItem(FLAG_KEY); } catch (e) {}

    if (cart.item_count === 0) {
      log('Rule A: cart already empty (order likely completed normally) — nothing to clear.');
      return false;
    }

    log('Rule A: cart has items and checkout was not completed — clearing cart and resetting session.');
    gismoOwnMutationInFlight = true;
    fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } })
      .then(function (res) {
        log('Rule A: /cart/clear.js response status =', res.status, '— resetting session and reloading.');
        resetSiteSession();
        reloadToReflectCart();
      })
      .catch(function (err) {
        log('Rule A: ERROR', err);
        gismoOwnMutationInFlight = false;
      });
    return true; // handled — caller should not also run Rule B against this same cart snapshot
  }

  // --- Rule B: a lone $0 item sitting alone in the cart — remove just that item ---
  function checkRuleB(cart) {
    if (!cart.items || cart.items.length !== 1) {
      log('Rule B: cart has', cart.items ? cart.items.length : 0, 'items — not a lone-item case, leaving it.');
      return;
    }
    var onlyItem = cart.items[0];
    if (onlyItem.final_line_price !== 0) {
      log('Rule B: sole cart item is not $0 (final_line_price=' + onlyItem.final_line_price + ') — leaving it.');
      return;
    }
    log('Rule B: sole cart item "' + onlyItem.product_title + '" is $0 and alone — removing it.');
    gismoOwnMutationInFlight = true;
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: onlyItem.key, quantity: 0 }),
    })
      .then(function (res) {
        log('Rule B: /cart/change.js response status =', res.status, '— reloading.');
        reloadToReflectCart();
      })
      .catch(function (err) {
        log('Rule B: ERROR', err);
        gismoOwnMutationInFlight = false;
      });
  }

  function checkCartHygiene() {
    if (onCheckoutPage()) {
      log('On a checkout page — both rules skipped.');
      return;
    }
    fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        if (!cart) return;
        log('/cart.js response: item_count =', cart.item_count, 'items =', cart.items && cart.items.length);
        var handledByRuleA = checkRuleA(cart);
        if (!handledByRuleA) checkRuleB(cart);
      })
      .catch(function (err) {
        log('ERROR fetching /cart.js:', err);
      });
  }

  // Rule A + B, driven by normal page views (fresh load or back/forward restore).
  window.addEventListener('pageshow', checkCartHygiene);

  // --- Rule B, immediate trigger: react the moment a cart-mutating request completes ---
  // (e.g. removing the paid item from the cart drawer) instead of waiting for the next page view.
  var CART_MUTATION_MARKERS = ['/cart/change', '/cart/update', '/cart/add', '/cart/clear'];

  function isCartMutationUrl(url) {
    if (!url) return false;
    return CART_MUTATION_MARKERS.some(function (marker) { return url.indexOf(marker) !== -1; });
  }

  function reactToCartMutation(url) {
    if (gismoOwnMutationInFlight) {
      log('Ignoring cart mutation to', url, '— it is this script\'s own request.');
      gismoOwnMutationInFlight = false;
      return;
    }
    if (onCheckoutPage()) return;
    log('Detected external cart mutation to', url, '— re-checking Rule B immediately.');
    fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        if (cart) checkRuleB(cart);
      })
      .catch(function (err) { log('ERROR re-checking cart after mutation:', err); });
  }

  var originalFetch = window.fetch;
  window.fetch = function (input) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var isMutation = isCartMutationUrl(url);
    var result = originalFetch.apply(this, arguments);
    if (isMutation) {
      result.then(function () { reactToCartMutation(url); }).catch(function () {});
    }
    return result;
  };

  var originalXhrOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this._gismoUrl = url;
    return originalXhrOpen.apply(this, arguments);
  };
  var originalXhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    var url = this._gismoUrl;
    if (isCartMutationUrl(url)) {
      this.addEventListener('loadend', function () { reactToCartMutation(url); });
    }
    return originalXhrSend.apply(this, arguments);
  };
})();
